import ImgUploaderByEntityId from 'components/ImgUploader/ImgUploaderByEntityId';
import Text from 'components/Text/Text';
import VideoUploaderByEntityId from 'components/VideoUploader/VideoUploaderByEntityId';
import { AppContext, IAppContext } from 'appState/appContext';
import { ITeasersContext, TeasersContext } from '../../teasersState/teasersContext';
import React, { useContext } from 'react';
import { RequestEntity } from 'types';
import breadcrumbs from 'images/breadcrumbs.svg';
import Button from 'components/Button/Button';
import { downloadBlob } from 'backend/additionalInformation';
import classNames from 'classnames';
import { createAppError } from 'utils/helpers';
import style from './TeaserPreviewModal.module.scss';

interface IVideoModal {
	isVisible: boolean;
	teaserIDForFullInfo: string;
	onClose: () => void;
}

const TeaserPreviewModal: React.FC<IVideoModal> = ({ isVisible, teaserIDForFullInfo, onClose }) => {
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const { teasers } = useContext<ITeasersContext>(TeasersContext);

	const containerClasses = classNames(style.container, { [style.visible]: isVisible });

	const teaserData = teasers.teasers.find((el) => el.id === teaserIDForFullInfo);

	const photoContentType =
		teaserData?.contentType === 'AdvertisingImage' ||
		teaserData?.contentType === 'AssistantImage' ||
		teaserData?.contentType === 'GameLaunchImage';

	const handleDownload = async () => {
		if (teaserData?.fileId) {
			showLoader();
			try {
				const file = await downloadBlob(teaserData.fileId);
				const href = URL.createObjectURL(file.data);

				const link = document.createElement('a');
				link.href = href;
				link.download = `teaser.${photoContentType ? '.jpg' : '.mp4'}`;
				document.body.appendChild(link);
				link.click();

				document.body.removeChild(link);
				URL.revokeObjectURL(href);
				hideLoader();
			} catch (err) {
				hideLoader();
				const error = err as Error;
				const appError = createAppError(error);
				addAppError(appError);
			}
		}
	};

	const renderUploader = () => {
		const videoContentType =
			teaserData?.contentType === 'AssistantVideo' ||
			teaserData?.contentType === 'AdvertisingVideo' ||
			teaserData?.contentType === 'GameLaunchVideo';
		const photoContentType =
			teaserData?.contentType === 'AssistantImage' ||
			teaserData?.contentType === 'AdvertisingImage' ||
			teaserData?.contentType === 'GameLaunchImage';

		if (videoContentType) {
			return (
				<VideoUploaderByEntityId
					loaderSize="big"
					isShowControls={true}
					resource={RequestEntity.Screensavers}
					entityID={teaserIDForFullInfo}
				/>
			);
		}
		if (photoContentType) {
			return (
				<ImgUploaderByEntityId
					resource={RequestEntity.Screensavers}
					entityID={teaserIDForFullInfo}
					photoSize="Original"
					loaderSize="big"
					noImageIdIcon="teaserDefaultAvatar"
				/>
			);
		}
		return (
			<VideoUploaderByEntityId
				loaderSize="big"
				isShowControls={true}
				resource={RequestEntity.Screensavers}
				entityID={teaserIDForFullInfo}
			/>
		);
	};

	return (
		<div className={containerClasses} data-test="TeaserPreviewModal">
			<div className={style.wrapper}>
				<div className={style.closeIconWrapper} onClick={onClose}>
					<svg className={style.closeIcon}>
						<use xlinkHref={`${breadcrumbs}#resetIcon`} />
					</svg>
				</div>
				<div className={style.content}>{teaserIDForFullInfo && renderUploader()}</div>
				<div className={style.buttonWrapper}>
					<Button
						onClick={handleDownload}
						icon={
							<svg className={style.downloadIcon}>
								<use xlinkHref={`${breadcrumbs}#downloadArrow`} />
							</svg>
						}>
						<Text translationNameSpace="listing" translationKey="downloadTeaserButton" asFragment={true} />
					</Button>
				</div>
			</div>
		</div>
	);
};

export default React.memo(TeaserPreviewModal);
